import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'

import {
  checkIfDateIsInTheFuture,
  checkIfEndDateIsAfterStart,
  checkTimeFormatValidForFurtherChecks,
  useSharedValidationRules
} from '@percent/cause-dashboard/common/hooks/useSharedValidationRules/useSharedValidationRules'
import { getDateFromDateAndTime } from '@percent/cause-dashboard/common/utility/getDateFromDateAndTime'
import type { YupSchema } from '@percent/cause-dashboard/common/utility/validation'
import { OpportunityLocationTypeDto } from '@percent/domain/volunteering'
import { isValidWebsiteURL } from '@percent/utility'

import type { OpportunityFormFields } from '../OpportunityFormFields.types'

type OpportunityFormSchema = YupSchema<OpportunityFormFields>

export const useOpportunityFormValidationSchema = () => {
  const { t } = useTranslation()
  const { validateString, validateNumber, validateTimeString } = useSharedValidationRules()

  return Yup.object<OpportunityFormSchema>().shape({
    name: validateString(),
    description: validateString({ maxLength: 5000 }),
    date: Yup.array().test(
      'dateIsFilled',
      t('validation.requiredField'),
      value => !!value && value.length === 2 && !!value[0] && !!value[1]
    ),
    startTime: validateTimeString()
      .required(t('validation.requiredField'))
      .test(
        'startTimeInTheFuture',
        t('validation.timeInTheFuture'),
        (value: string | undefined, context: Yup.TestContext) => {
          const { date } = context.parent as { date?: [Date, Date] | [null, null] }

          if (!!date?.[0] && value && checkTimeFormatValidForFurtherChecks(value)) {
            return checkIfDateIsInTheFuture(getDateFromDateAndTime(date[0], value))
          }

          return true
        }
      ),
    endTime: validateTimeString()
      .required(t('validation.requiredField'))
      .test(
        'endTimeAfterStartTime',
        t('validation.endTimeAfterStartTime'),
        (value: string | undefined, context: Yup.TestContext) => {
          const { date, startTime } = context.parent as {
            date?: [Date, Date] | [null, null]
            startTime: string | undefined
          }

          if (
            date?.some(d => !!d) &&
            startTime &&
            checkTimeFormatValidForFurtherChecks(startTime) &&
            value &&
            checkTimeFormatValidForFurtherChecks(value)
          ) {
            return checkIfEndDateIsAfterStart(
              getDateFromDateAndTime(date[0] as Date, startTime),
              getDateFromDateAndTime(date[1] as Date, value)
            )
          }

          return true
        }
      ),
    locationType: validateString(),
    locationUrl: Yup.string().when('locationType', {
      is: (locationType: string) => locationType === OpportunityLocationTypeDto.Virtual,
      then: () =>
        isValidWebsiteURL(t('validation.url'))
          .trim()
          .min(1, t('validation.requiredField'))
          .max(2048, t('validation.maxCharacters', { max: 2048 }))
    }),
    addressLineOne: Yup.string().when('locationType', {
      is: OpportunityLocationTypeDto.Offline,
      then: () => validateString()
    }),
    addressLineTwo: Yup.string().when('locationType', {
      is: OpportunityLocationTypeDto.Offline,
      then: () => validateString({ optional: true })
    }),
    city: Yup.string().when('locationType', {
      is: OpportunityLocationTypeDto.Offline,
      then: () => validateString()
    }),
    zipCode: Yup.string().when('locationType', {
      is: OpportunityLocationTypeDto.Offline,
      then: () => validateString()
    }),
    country: Yup.string().when('locationType', {
      is: OpportunityLocationTypeDto.Offline,
      then: () => validateString()
    }),
    participantSpots: validateNumber({ max: 100000, optional: true }),
    activities: Yup.array()
      .of(Yup.string())
      .min(1, t('validation.requiredField'))
      .required(t('validation.requiredField')),
    skills: Yup.array().of(Yup.string()),
    manualTimeTracking: Yup.boolean().default(false),
    notificationsEnabled: Yup.boolean().default(true),
    long: Yup.number(),
    lat: Yup.number(),
    targetCountries: Yup.array().of(Yup.string())
  })
}
